import { useQuery } from '@tanstack/react-query'
import { useSession } from '@/features/auth/context/session.context'
import { UserRole } from '@/shared/constants/user-roles'

const GROUP_CONTACT_ROLES = [UserRole.VOLUNTEER, UserRole.TEAM_LEADER, UserRole.PARTNER]

import { authFetch } from '@/shared/lib/auth-fetch'

async function fetchIsGroupContact(): Promise<{ isGroupContact: boolean; inAnyGroup: boolean }> {
  const res = await authFetch('/api/stm/me/is-group-contact')
  if (!res.ok) throw new Error('Failed to fetch')
  const json = await res.json()
  return {
    isGroupContact: !!json?.isGroupContact,
    inAnyGroup: !!json?.inAnyGroup,
  }
}

export function useIsGroupContact() {
  const { session, isLoading: sessionLoading } = useSession()
  const role = session?.role as UserRole | undefined

  const isGroupContactRole = role && GROUP_CONTACT_ROLES.includes(role)
  const needsCheck = isGroupContactRole && !sessionLoading

  const { data, isLoading } = useQuery({
    queryKey: ['stm', 'is-group-contact'],
    queryFn: fetchIsGroupContact,
    enabled: needsCheck,
    staleTime: 60_000,
  })

  if (sessionLoading) {
    return { isGroupContact: false, inAnyGroup: false, isLoading: true }
  }
  if (!isGroupContactRole) {
    const hasAccess = role === UserRole.ADMIN || role === UserRole.BASE_LEADER
    return { isGroupContact: hasAccess, inAnyGroup: hasAccess, isLoading: false }
  }
  return {
    isGroupContact: !!data?.isGroupContact,
    inAnyGroup: !!data?.inAnyGroup,
    isLoading: needsCheck && isLoading,
  }
}
